import * as base64Buffer from "base64-arraybuffer";
import { getUint16Value, getUint8Value } from "@/utils/ByteUtils";
import {
  ComBoxSensorData,
  ComBoxSensorType,
  getSensorData,
  ROOM_SENSOR_INDEX,
} from "./ComBoxSensorData";
import { RunOverview } from "./RunOverview";
import { calculatePowerConsumption } from "./ComBoxHumidityUnit";

export interface SampleInfo {
  sampleIndex: number;
  sampleTimeInMinutes: number;
}

export interface ComBoxSampleInfo extends SampleInfo {
  guideSensor: number;
  step: number;
  // TODO: In upper 4 bits after event bit, there is an information about the device state 
  // and this can be used for identifying the pause state.
  event: number;
  sensorData: ComBoxSensorData[];
  powerConsumption?: number;
}

export function isGeneratedPowerFailSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return (
    comBoxSampleInfo.event === 31 && comBoxSampleInfo.sampleIndex === 65535
  );
}

export function isNightPauseStartSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 20;
}

export function isNightPauseEndSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 21;
}

export function isDryingPauseStartSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 24;
}

export function isDryingPauseEndSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 25;
}

export function isPauseStartSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return isDryingPauseStartSample(comBoxSampleInfo) || isNightPauseStartSample(comBoxSampleInfo);
}

export function isPauseEndSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return isDryingPauseEndSample(comBoxSampleInfo) || isNightPauseEndSample(comBoxSampleInfo);
}

export function isUartStatusSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 74;
}

export function isConfigurationInfoSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 64;
}

export function isConfigurationTypeInfoSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 67;
}

export function isDryingPauseInfoSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 27;
}

export function isMonitorSample(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.event === 22;
}

export function isRunReachedMaxDurationSample(
  comBoxSampleInfo: ComBoxSampleInfo
) {
  return comBoxSampleInfo.event === 17;
}

export function isGuideSensorSet(guideSensorIndex: number) {
  return guideSensorIndex !== 255 && guideSensorIndex !== 15;
}

export function hasGuideSensor(comBoxSampleInfo: ComBoxSampleInfo) {
  return isGuideSensorSet(comBoxSampleInfo.guideSensor);
}

export function getFloorSampleData(comBoxSampleInfo: ComBoxSampleInfo) {
  return comBoxSampleInfo.sensorData.filter(
    (data) => data.sensorIndex !== ROOM_SENSOR_INDEX
  );
}

export function getRoomSampleData(comBoxSampleInfo: ComBoxSampleInfo) {
  return [comBoxSampleInfo.sensorData[4]];
}

export interface PanelSampleInfo extends SampleInfo {
  heatRatioPercentage?: number;
  wallTemp: number;
  maxWallTempStep?: number;
  groundTemp?: number;
  maxGroundTempStep?: number;
}

export function hasV2OrGreaterSampleData(runOverview: RunOverview) {
  const hasV2SampleData = runOverview.sampleFormatVersion === 2;
  const hasV3SampleData = runOverview.sampleFormatVersion === 3;

  return hasV2SampleData || hasV3SampleData;
}

export function createComBoxSampleFromBase64(
  data: string,
  runOverview: RunOverview,
  removeOutlierSamples: boolean
): ComBoxSampleInfo | null {
  if (!data) {
    return null;
  }

  const array = new Uint8Array(base64Buffer.decode(data));

  if (!array) {
    return null;
  }

  const sampleIndex = getUint16Value(array, 0);
  const event = getUint8Value(array, 2);

  if (event == 255) {
    return null;
  }

  const sampleFormatVersionOffset = hasV2OrGreaterSampleData(runOverview) ? 3 : 0;

  const sampleTimeInMinutes = getUint16Value(array, hasV2OrGreaterSampleData(runOverview) ? 7 : 4);
  
  const encodedPower = 
    array.length >= (6 + sampleFormatVersionOffset + 2) 
      ? getUint16Value(
        array,
        6 + sampleFormatVersionOffset
      ) 
      : getUint8Value(
        array,
        6 + sampleFormatVersionOffset
      );
  const powerConsumption = calculatePowerConsumption(encodedPower);

  const combiState = getUint8Value(array, 3);

  const guideSensor = hasV2OrGreaterSampleData(runOverview)
    ? getUint8Value(array, 4)
    : (combiState >>> 6) % 4;

  const programAndStep = hasV2OrGreaterSampleData(runOverview) ? getUint8Value(array, 5) : 0;
  const step = (programAndStep >>> 4) % 16;

  const sensorData = [];

  let sensorDataArrayIndex = hasV2OrGreaterSampleData(runOverview) ? 11 : 8;

  const sensorConfig = runOverview.sensorConfig;

  for (let sensorIndex = 0; sensorIndex < sensorConfig.length; sensorIndex++) {
    const sensorType = sensorConfig[sensorIndex];

    if (
      sensorType !== ComBoxSensorType.EMPTY &&
      sensorType !== ComBoxSensorType.UNKNOWN
    ) {
      const data = getSensorData(
        sensorType,
        sensorIndex,
        sensorDataArrayIndex,
        array
      );

      if (
        removeOutlierSamples &&
        data.temperature !== undefined &&
        (data.temperature < 0 || data.temperature >= 60)
      ) {
        sensorData.push({ sensorIndex: sensorIndex });
      } else {
        sensorData.push(data);
      }

      sensorDataArrayIndex += 2;
    } else {
      sensorData.push({ sensorIndex: sensorIndex });
    }
  }

  return {
    step: step,
    sampleIndex: sampleIndex,
    sampleTimeInMinutes: sampleTimeInMinutes,
    event: event,
    guideSensor: guideSensor,
    sensorData: sensorData,
    powerConsumption: powerConsumption
  };
}

export function createPanelSampleFromBase64(
  data: string,
  sampleIndex: number,
  isSampleOfEdgePanel: boolean,
  isActiveRun: boolean
): PanelSampleInfo | null {
  const array = new Uint8Array(base64Buffer.decode(data));

  const sampleTimeInMinutes = getUint16Value(array, 0, true);
  const wallTemp = getUint8Value(array, 2);

  if (wallTemp >= 255) {
    return null;
  }

  const maxWallTempStep = isActiveRun ? getUint8Value(array, 3) : wallTemp;
  const heatRatio = isActiveRun
    ? (getUint8Value(array, array.length - 1) / 255) * 100
    : undefined;

  let groundTemp: number | undefined = undefined;
  let maxGroundTempStep: number | undefined = undefined;

  if (isSampleOfEdgePanel) {
    groundTemp = isActiveRun
      ? getUint8Value(array, 4)
      : getUint8Value(array, 3);
    maxGroundTempStep = isActiveRun ? getUint8Value(array, 5) : groundTemp;
  }

  return {
    sampleIndex: sampleIndex,
    sampleTimeInMinutes: sampleTimeInMinutes,
    wallTemp: wallTemp,
    maxWallTempStep: maxWallTempStep,
    groundTemp: groundTemp,
    maxGroundTempStep: maxGroundTempStep,
    heatRatioPercentage: heatRatio,
  };
}

// ==================== FIRMWARE v4.0.0+ CHANGES ====================
/**
 * Calculates the byte offset where sensor data begins within a sample frame.
 * 
 * - v1: Sensor data starts at index +8
 * - v2 or higher: Sensor data starts at index +11 to accommodate extended metadata fields.
 * 
 * Firmware v4.0.0+ continues this pattern with support for v3 samples.
 */
export function getSensorDataStartIndexFromSampleFormatVersion(
  startIndex: number, 
  sampleFormatVersion = 2
) { 
  if (sampleFormatVersion >= 2) {
    return startIndex + 11;
  }
  else {
    return startIndex + 8;
  }
}